import React, { useCallback, useEffect, useRef, useState } from 'react';

export interface MarqueeRect {
  left: number;
  top: number;
  width: number;
  height: number;
}

interface ViewportRect {
  x: number;
  y: number;
  width: number;
  height: number;
}

function rectsOverlap(a: ViewportRect, b: DOMRect): boolean {
  return a.x < b.right && a.x + a.width > b.left && a.y < b.bottom && a.y + a.height > b.top;
}

export function useMarqueeSelection(
  containerRef: React.RefObject<HTMLDivElement | null>,
  setSelected: React.Dispatch<React.SetStateAction<Set<string>>>
) {
  const [marqueeRect, setMarqueeRect] = useState<MarqueeRect | null>(null);
  const startPoint = useRef<{ x: number; y: number } | null>(null);
  const modifierRef = useRef<{ shift: boolean; meta: boolean }>({ shift: false, meta: false });
  const baseSelectionRef = useRef<Set<string>>(new Set());

  const getIntersectingPhotoIds = useCallback(
    (rect: ViewportRect): Set<string> => {
      const ids = new Set<string>();
      if (!containerRef.current) return ids;
      const tiles = containerRef.current.querySelectorAll<HTMLElement>('[data-photo-id]');
      tiles.forEach((tile) => {
        const photoId = tile.getAttribute('data-photo-id');
        if (photoId && rectsOverlap(rect, tile.getBoundingClientRect())) {
          ids.add(photoId);
        }
      });
      return ids;
    },
    [containerRef]
  );

  const onContainerMouseDown = useCallback(
    (e: React.MouseEvent) => {
      if (e.button !== 0) return;
      const target = e.target as HTMLElement;
      if (target.closest('[data-photo-id]')) return;

      startPoint.current = { x: e.clientX, y: e.clientY };
      modifierRef.current = { shift: e.shiftKey, meta: e.metaKey || e.ctrlKey };

      setSelected((prev) => {
        baseSelectionRef.current = new Set(prev);
        return prev;
      });
    },
    [setSelected]
  );

  useEffect(() => {
    const onMouseMove = (e: MouseEvent) => {
      if (!startPoint.current) return;
      const container = containerRef.current;
      if (!container) return;

      const sx = startPoint.current.x;
      const sy = startPoint.current.y;
      const viewportRect: ViewportRect = {
        x: Math.min(sx, e.clientX),
        y: Math.min(sy, e.clientY),
        width: Math.abs(e.clientX - sx),
        height: Math.abs(e.clientY - sy),
      };

      if (viewportRect.width < 5 && viewportRect.height < 5) return;

      const containerBounds = container.getBoundingClientRect();
      setMarqueeRect({
        left: viewportRect.x - containerBounds.left + container.scrollLeft,
        top: viewportRect.y - containerBounds.top + container.scrollTop,
        width: viewportRect.width,
        height: viewportRect.height,
      });

      const intersecting = getIntersectingPhotoIds(viewportRect);
      const base = baseSelectionRef.current;

      if (modifierRef.current.shift) {
        setSelected(new Set([...base, ...intersecting]));
      } else if (modifierRef.current.meta) {
        const next = new Set(base);
        intersecting.forEach((id) => {
          if (base.has(id)) next.delete(id);
          else next.add(id);
        });
        setSelected(next);
      } else {
        setSelected(intersecting);
      }
    };

    const onMouseUp = () => {
      startPoint.current = null;
      setMarqueeRect(null);
    };

    document.addEventListener('mousemove', onMouseMove);
    document.addEventListener('mouseup', onMouseUp);
    return () => {
      document.removeEventListener('mousemove', onMouseMove);
      document.removeEventListener('mouseup', onMouseUp);
    };
  }, [containerRef, getIntersectingPhotoIds, setSelected]);

  return { marqueeRect, onContainerMouseDown };
}
